import React, { useState } from 'react';
import { Button, Col, Container, Icon, Loader, Row, SelectInput } from '@digitools/honest-react';
import { Language } from '../../../types/Language';
import { DocumentUploadDisplayTextEs } from '../../../components/documentUpload/language/DocumentUploadDisplayTextEs';
import { DocumentUploadDisplayTextEn } from '../../../components/documentUpload/language/DocumentUploadDisplayTextEn';
import styled from 'styled-components';
import useAuthentication from '../../../hooks/useAuthentication';
import useExperience from '../../../hooks/useExperience';
import useTealium from '../../../hooks/useTealium';
import { TEALIUM_EVENT } from '../../../constants/tealiumConstants';
import { CustomEmployeeText } from 'src/utils/CustomExperienceText';
import { Experience } from 'src/types/Experience';
import useCliDataContext from '../hooks/useCliDataContext';
import { CategoryCol, StyledSerial } from 'src/components/documentUpload/CategorizationDocumentUpload';
import { docMeta } from 'src/packages/status/components/pages/ClaimDetails/docCategoryMapper';
import SelectCategory from 'src/components/documentUpload/SelectCategory';
import useTranslate from 'src/hooks/useTranslate';
import useCliCustomerConfigContext from 'src/packages/cli/hooks/useCliContext';
import useDocumentUpload from 'src/components/documentUpload/useDocumentUpload';
import UploadErrorAlerts from 'src/components/documentUpload/UploadErrorAlerts';
const StyledCol = styled(Col) `
  padding: 12px;
`;
const StyledDeleteButton = styled(Button) `
  font-weight: bold;
  padding: 0;
  height: auto;
  &:hover {
    text-decoration: underline;
  }
`;
const CliDocumentUpload = ({ productName }) => {
    const { language, experience } = useExperience();
    const { trackEvent } = useTealium();
    // Guard analytics so a Tealium failure can't break the upload success/error flow.
    const safeTrackEvent = (params) => {
        try {
            trackEvent(params);
        }
        catch (error) {
            console.error('Tealium trackEvent failed', error);
        }
    };
    const { authHeader, user } = useAuthentication();
    const languageFile = language === Language.SPANISH ? DocumentUploadDisplayTextEs : DocumentUploadDisplayTextEn;
    const { uploadedFiles, setUploadedFiles } = useCliDataContext();
    const [documentCategory, setDocumentCategory] = useState('');
    const { t } = useTranslate();
    const { isClaimLeaveCustomer } = useCliCustomerConfigContext();
    let isCoordinatedClaim = false;
    //determine if it should see Claim categories or Coordinated categories
    if (productName === 'STD' && isClaimLeaveCustomer) {
        isCoordinatedClaim = true;
    }
    let productType = docMeta.s1.noCategory;
    if (user?.customerInContext?.smallMid && experience === Experience.EMPLOYEE) {
        if (productName === 'STD') {
            productType = docMeta.docsNet.claimsEE;
        }
    }
    else if (experience === Experience.EMPLOYEE) {
        if (productName === 'LEAVE') {
            productType = docMeta.s1.leaves;
        }
        else if (isCoordinatedClaim) {
            productType = docMeta.s1.coordinatedEE;
        }
        else {
            productType = docMeta.s1.claimsEE;
        }
    }
    else if (experience === Experience.EMPLOYER) {
        if (productName === 'LEAVE') {
            productType = docMeta.s1.leaves;
        }
        else if (isCoordinatedClaim) {
            productType = docMeta.s1.coordinatedER;
        }
        else {
            productType = docMeta.s1.claimsER;
        }
    }
    const getDocMeta = (val) => {
        const metaObj = t(productType).filter((e) => e.label == val);
        return metaObj[0];
    };
    const { errorMessage, apiFailureMessage, uploading, onUpload } = useDocumentUpload({
        languageFile,
        interviewType: productName.toUpperCase(),
        experience,
        authHeader: authHeader(),
        onUploadSuccess: (response, file) => {
            const docMeta = documentCategory && getDocMeta(documentCategory);
            setUploadedFiles([
                ...uploadedFiles,
                {
                    documentName: file.name,
                    docId: response.data,
                    label: docMeta.label,
                    documentTypeTitle: docMeta.title,
                    documentType: docMeta.type,
                },
            ]);
            setDocumentCategory('');
        },
        onValidationError: (type, message) => {
            // Empty-file rejections did not previously fire an analytics event.
            if (type === 'empty') {
                return;
            }
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.ERROR,
                event_type: TEALIUM_EVENT.EVENT_TYPE.CLAIM_LEAVE_INTAKE,
                event_name: message,
                event_version: 'upload document',
            });
        },
        onApiError: () => {
            setDocumentCategory('');
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.ERROR,
                event_type: TEALIUM_EVENT.EVENT_TYPE.CLAIM_LEAVE_INTAKE,
                event_name: languageFile.apiFailureMessage + languageFile.contactUs,
                event_version: 'upload document',
            });
        },
    });
    const renderCategoryColumns = () => {
        return (<>
        {uploadedFiles?.map((documentDetail, index) => {
                return (<>
              <CategoryCol md={5}>
                <StyledSerial>{index + 1}</StyledSerial>
                <SelectInput key={documentDetail.docId} field={'doc-category'} options={t(productType)} data-testid={'doc-category'} initialValue={documentDetail?.label} onChange={(e) => updateCategory(documentDetail, e.target.value)}/>
              </CategoryCol>

              <Col md={7}> {getCategoryDocuments(documentDetail)}</Col>
            </>);
            })}
      </>);
    };
    const getCategoryDocuments = (documentDetail) => {
        const handleDelete = () => {
            const newDocumentList = uploadedFiles.filter(file => file.docId !== documentDetail.docId);
            setUploadedFiles(newDocumentList);
        };
        return (<Row key={documentDetail.docId}>
        <StyledCol md={8}>
          <Row data-testid={'submitted-doc'} aria-labelledby={documentDetail.documentName}>
            <Icon style={{ margin: '2px .5rem 0 1rem' }} name='documents'/> {documentDetail.documentName}
          </Row>
        </StyledCol>
        <StyledCol md={3}>
          <StyledDeleteButton data-testid={'delete-doc'} aria-label={languageFile.delete} onClick={handleDelete}>
            <Icon color='secondary' name='trash'/> {languageFile.delete}
          </StyledDeleteButton>
        </StyledCol>
      </Row>);
    };
    const updateCategory = (docDetails, val) => {
        let updatedCategory = uploadedFiles.map(e => {
            let metaDoc = getDocMeta(val);
            if (e.docId == docDetails.docId) {
                return { ...e, label: metaDoc.label, documentTypeTitle: metaDoc.title, documentType: metaDoc.type };
            }
            return e;
        });
        setUploadedFiles(updatedCategory);
    };
    const documentUploadLabel = CustomEmployeeText(experience === Experience.EMPLOYEE
        ? `${languageFile.informationalMessage1} ${languageFile.yourAbsenceEE}`
        : `${languageFile.informationalMessage1} ${languageFile.yourAbsenceER}`);
    return (<div data-testid='cli-document-upload'>
      <Loader id='document-upload-spinner' isShown={uploading}>
        <Container>
          <UploadErrorAlerts errorMessage={errorMessage} apiFailureMessage={apiFailureMessage} languageFile={languageFile}/>
          <Row data-testid={'informationText'} style={{ marginTop: '1rem' }}>
            {documentUploadLabel}
          </Row>
          <Row data-testid={'acceptableDocumentsText'}>
            {languageFile.informationalMessage2}
          </Row>
          {uploadedFiles?.length < 10 &&
            <>
                <Row>
                  <p style={{ fontWeight: 'bold', fontSize: 'larger' }}>{languageFile.uploadCategory}</p>
                </Row>
                <Row style={{ marginBottom: '0.5rem' }}>{renderCategoryColumns()}</Row>
                <Row data-testid={'Category'} style={{ marginTop: '1rem' }}>
                  <SelectCategory onUpload={onUpload} eventNum={''} uploadedFiles={uploadedFiles} documentCategory={documentCategory} uploading={uploading} categoryList={productType} setDocumentCategory={setDocumentCategory}/>
                </Row>
              </>}
        </Container>
      </Loader>
    </div>);
};
export default CliDocumentUpload;
